import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './style.module.css';

const MotivationStep = ({
  onNext,
  onBack,
  selectedMotivations: initialMotivations = [],
  stepData,
}) => {
  const [selectedMotivations, setSelectedMotivations] = useState(
    Array.isArray(initialMotivations) ? initialMotivations : []
  );

  // Get motivation options from API
  const motivationOptions =
    stepData?.options
      ?.map(option => ({
        id: option.value || option.label, // Use value as ID, fallback to label
        label: option.label,
        value: option.value,
        displayOrder: option.displayOrder,
      }))
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)) || [];

  // Initialize selectedMotivations from stepData if user has previously answered
  useEffect(() => {
    // Priority 1: Always check current session data first (user made changes in this session)
    // If session data exists (even if empty), it should take priority over API data
    if (
      initialMotivations !== null &&
      initialMotivations !== undefined &&
      Array.isArray(initialMotivations)
    ) {
      // Filter out hubspotSkipValue from session data
      const filteredSessionData = initialMotivations.filter(
        motivation => motivation !== stepData?.hubspotSkipValue
      );

      setSelectedMotivations(filteredSessionData);
    }
    // Priority 2: Only use API data if no session data exists (browser refresh scenario only)
    else if (
      stepData?.hasAnswered &&
      stepData?.userResponse &&
      Array.isArray(stepData.userResponse)
    ) {
      // Check if userResponse contains only the hubspotSkipValue
      const isSkippedValue =
        stepData.userResponse.length === 1 &&
        stepData.userResponse[0] === stepData?.hubspotSkipValue;

      if (isSkippedValue) {
        // If it's a skipped value, set empty array (no selections)
        setSelectedMotivations([]);
      } else {
        // Filter out hubspotSkipValue values
        const filteredUserResponse = stepData.userResponse.filter(
          value => value !== stepData?.hubspotSkipValue
        );

        const userResponseIds = filteredUserResponse.map(value => {
          // Find the motivation option that has this value
          const matchingOption = motivationOptions.find(option => option.value === value);
          return matchingOption?.id || value; // Use ID if found, fallback to value
        });

        setSelectedMotivations(userResponseIds);
      }
    }
  }, [
    stepData?.hasAnswered,
    stepData?.userResponse,
    stepData?.hubspotSkipValue,
    motivationOptions.length,
  ]); // Include motivationOptions.length to wait for API data

  const handleMotivationToggle = motivationId => {
    setSelectedMotivations(prevSelected => {
      // Ensure prevSelected is always an array
      const currentSelection = Array.isArray(prevSelected) ? prevSelected : [];

      // If already selected, remove it
      if (currentSelection.includes(motivationId)) {
        return currentSelection.filter(id => id !== motivationId);
      }

      // Get maxInput from API, fallback to 3
      const maxInput = stepData?.validations?.maxInput;

      // If we already have maxInput selections and trying to add more, don't add
      if (currentSelection.length >= maxInput) {
        return currentSelection;
      }

      // Otherwise, add it
      return [...currentSelection, motivationId];
    });
  };

  const handleNextClick = () => {
    if (onNext && selectedMotivations.length > 0) {
      // Convert selected IDs to actual values for API submission
      const selectedValues = selectedMotivations.map(motivationId => {
        const motivationObject = motivationOptions.find(
          motivation => motivation.id === motivationId
        );
        return motivationObject?.value || motivationId; // Use value from API, fallback to ID
      });
      onNext(selectedValues);
    }
  };

  const handleSkipClick = () => {
    if (onNext) {
      // Clear selections immediately for UI feedback
      setSelectedMotivations([]);

      // Small delay to ensure state update is visible before navigation
      setTimeout(() => {
        // Use hubspotSkipValue from API if available, otherwise send empty array
        if (stepData?.hubspotSkipValue) {
          onNext([stepData.hubspotSkipValue]);
        } else {
          onNext([]);
        }
      }, 50);
    }
  };

  // Validation function based on API minInput/maxInput
  const isNextButtonEnabled = () => {
    const minInput = stepData?.validations?.minInput;
    const maxInput = stepData?.validations?.maxInput;

    const selectionCount = selectedMotivations.length;

    // Check minimum requirement
    if (minInput > 0 && selectionCount < minInput) {
      return false;
    }

    // Check maximum requirement (though UI already handles this with disabled cards)
    if (selectionCount > maxInput) {
      return false;
    }

    return true;
  };

  // Get dynamic maxInput value for rendering logic
  const maxInput = stepData?.validations?.maxInput;

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <h2 className={styles.header}>{stepData?.title}</h2>
        {stepData?.subtitle && <p className={styles.subHeader}>{stepData.subtitle}</p>}
      </div>

      <div className={styles.motivationsContainer}>
        {motivationOptions.map(motivation => (
          <div
            key={motivation.id}
            className={`${styles.motivationCard} ${
              selectedMotivations.includes(motivation.id) ? styles.selectedCard : ''
            } ${
              selectedMotivations.length >= maxInput &&
              !selectedMotivations.includes(motivation.id)
                ? styles.disabledCard
                : ''
            }`}
            onClick={() => {
              // Only allow selection if card is not disabled
              if (
                !(
                  selectedMotivations.length >= maxInput &&
                  !selectedMotivations.includes(motivation.id)
                )
              ) {
                handleMotivationToggle(motivation.id);
              }
            }}
          >
            <span className={styles.motivationText}>{motivation.label}</span>
          </div>
        ))}
      </div>

      <div className={styles.buttonsContainer}>
        <div className={styles.buttonGroup}>
          <button type="button" className={styles.skipButton} onClick={handleSkipClick}>
            Skip
          </button>
          <button
            type="button"
            className={styles.nextButton}
            disabled={!isNextButtonEnabled()}
            onClick={handleNextClick}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

MotivationStep.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func,
  selectedMotivations: PropTypes.arrayOf(PropTypes.string),
  stepData: PropTypes.object,
};

MotivationStep.defaultProps = {
  onBack: () => {},
  selectedMotivations: [],
};

export default MotivationStep;
